// BEAM UI primitive — Field.
// Label (small, muted) + input with a 3px focus ring. Min height 44, radius.sm.
// Optional error/help line. Consumes only theme tokens.
//
// `secureToggle` opt-in: renders an eye / eye-off button that shows or hides the typed value
// (password reveal). When set, the Field OWNS secureTextEntry (starts hidden) — callers do not also
// pass secureTextEntry. Non-toggle fields render exactly as before (single input, no extra layout).
import React, { useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
  type StyleProp,
  type TextInputProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import Svg, { Path, Line, Circle } from 'react-native-svg';
import { theme } from '../../theme';
import { Text } from './Text';

// On web, react-native-web renders TextInput as an <input> that keeps the browser's default focus
// outline — which draws a second box inside our own focus border ("box in a box"). Suppress it; the
// Field already provides its own focus ring via `styles.focused`. (CSS-only prop; cast for RN types.)
const webOutlineReset: TextStyle | undefined = Platform.OS === 'web' ? ({ outlineStyle: 'none' } as unknown as TextStyle) : undefined;

export type FieldProps = TextInputProps & {
  label?: string;
  error?: string;
  help?: string;
  containerStyle?: StyleProp<ViewStyle>;
  /** Render an eye / eye-off reveal button. When set, the Field manages secureTextEntry itself. */
  secureToggle?: boolean;
};

/** Eye (visible) / eye-off (hidden) glyph — Feather-style strokes drawn with react-native-svg. */
function EyeIcon({ off, color }: { off: boolean; color: string }) {
  return (
    <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
      {off ? (
        <>
          <Path
            d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Line x1={1} y1={1} x2={23} y2={23} stroke={color} strokeWidth={2} strokeLinecap="round" />
        </>
      ) : (
        <>
          <Path
            d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Circle cx={12} cy={12} r={3} stroke={color} strokeWidth={2} />
        </>
      )}
    </Svg>
  );
}

export function Field({ label, error, help, containerStyle, style, onFocus, onBlur, secureToggle, secureTextEntry, ...rest }: FieldProps) {
  const [focused, setFocused] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // When the reveal toggle is on, the Field owns secrecy (starts hidden); otherwise honor the prop.
  const secure = secureToggle ? !revealed : secureTextEntry;

  return (
    <View style={[styles.container, containerStyle]}>
      {label ? (
        <Text variant="small" color="secondary">
          {label}
        </Text>
      ) : null}
      <View
        style={[
          styles.inputWrap,
          secureToggle && styles.inputWrapRow,
          focused && styles.focused,
          !!error && styles.errored,
        ]}
      >
        <TextInput
          style={[styles.input, secureToggle && styles.inputFlex, webOutlineReset, style]}
          placeholderTextColor={theme.color.text.secondary}
          secureTextEntry={secure}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          {...rest}
        />
        {secureToggle ? (
          <Pressable
            onPress={() => setRevealed((v) => !v)}
            style={styles.reveal}
            accessibilityRole="button"
            accessibilityLabel={revealed ? 'Hide password' : 'Show password'}
            hitSlop={8}
          >
            <EyeIcon off={revealed} color={theme.color.text.secondary} />
          </Pressable>
        ) : null}
      </View>
      {error ? (
        <Text variant="small" color="danger">
          {error}
        </Text>
      ) : help ? (
        <Text variant="small" color="secondary">
          {help}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: theme.spacing.xs },
  inputWrap: {
    minHeight: theme.layout.fieldHeight,
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.sm,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.strong,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.md,
  },
  // Reveal layout: input + eye button on one row (only applied when secureToggle is set).
  inputWrapRow: { flexDirection: 'row', alignItems: 'center' },
  inputFlex: { flex: 1 },
  focused: {
    borderColor: theme.color.action.primary,
    // Soft 3px focus ring via a matching shadow/outline tint.
    borderWidth: theme.layout.focusRingWidth,
    paddingHorizontal: theme.spacing.md - (theme.layout.focusRingWidth - theme.layout.hairline),
  },
  errored: { borderColor: theme.color.action.danger },
  input: {
    ...theme.type.body,
    color: theme.color.text.primary,
    paddingVertical: theme.spacing.sm,
  },
  reveal: {
    paddingLeft: theme.spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
